/**
 * Taxation schemes ref management (Підпорція 3.1a, 2026-05-24).
 *
 * Global довідник з 6 seed-схемами (GENERAL/SIMPLIFIED_3_3/3_5/4/FOP_1/2).
 * Користувач може додавати власні. Seed захищений у handler.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  taxationSchemes,
  type TaxationScheme,
} from '@/api/taxation';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const SEED_CODES = new Set([
  'GENERAL', 'SIMPLIFIED_3_3', 'SIMPLIFIED_3_5',
  'SIMPLIFIED_4', 'FOP_1', 'FOP_2',
]);

export default function TaxationSchemesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const list = useQuery({
    queryKey: ['taxation-schemes'],
    queryFn: () => taxationSchemes.list(),
  });

  const [editing, setEditing] = useState<TaxationScheme | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: '', name_uk: '', has_vat: true,
    profit_tax_rate: '', single_tax_rate: '', description: '',
  });
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      taxationSchemes.create({
        code: form.code,
        name_uk: form.name_uk,
        has_vat: form.has_vat,
        profit_tax_rate: form.profit_tax_rate || null,
        single_tax_rate: form.single_tax_rate || null,
        description: form.description || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['taxation-schemes'] });
      setCreating(false);
    },
    onError: (e) => setError(extractError(e)),
  });

  const updateMut = useMutation({
    mutationFn: () =>
      taxationSchemes.update(editing!.id, {
        name_uk: form.name_uk,
        has_vat: form.has_vat,
        profit_tax_rate: form.profit_tax_rate || null,
        single_tax_rate: form.single_tax_rate || null,
        description: form.description || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['taxation-schemes'] });
      setEditing(null);
    },
    onError: (e) => setError(extractError(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => taxationSchemes.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['taxation-schemes'] }),
    onError: (e) => setError(extractError(e)),
  });

  function startEdit(s: TaxationScheme) {
    setEditing(s);
    setCreating(false);
    setForm({
      code: s.code, name_uk: s.name_uk, has_vat: s.has_vat,
      profit_tax_rate: s.profit_tax_rate ?? '',
      single_tax_rate: s.single_tax_rate ?? '',
      description: s.description ?? '',
    });
    setError(null);
  }

  const cols = useMemo<ColumnDef<TaxationScheme>[]>(() => [
    {
      key: 'code', header: 'Код', width: 160,
      sortValue: (s) => s.code, cell: (s) => <code>{s.code}</code>,
    },
    {
      // Без width — гнучка колонка: займає вільний простір.
      key: 'name_uk', header: 'Назва',
      sortValue: (s) => s.name_uk.toLowerCase(), cell: (s) => s.name_uk,
    },
    {
      key: 'has_vat', header: 'ПДВ', width: 80, align: 'center',
      sortValue: (s) => (s.has_vat ? 1 : 0),
      cell: (s) => (s.has_vat ? '✓' : '—'),
    },
    {
      key: 'profit_tax_rate', header: 'Прибуток %', width: 120, align: 'right',
      sortValue: (s) => Number(s.profit_tax_rate ?? -1),
      cell: (s) => s.profit_tax_rate ?? '—',
    },
    {
      key: 'single_tax_rate', header: 'ЄП %', width: 110, align: 'right',
      sortValue: (s) => Number(s.single_tax_rate ?? -1),
      cell: (s) => s.single_tax_rate ?? '—',
    },
    {
      key: 'actions', header: 'Дії', width: 200,
      cell: (s) => (
        <>
          <button onClick={() => startEdit(s)}>Редагувати</button>
          <button
            className="danger"
            disabled={SEED_CODES.has(s.code)}
            onClick={() =>
              confirm('Видалити схему?').then((ok) => ok && deleteMut.mutate(s.id))
            }
          >
            Видалити
          </button>
        </>
      ),
    },
  ], [confirm, deleteMut]);

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Системи оподаткування</h2>
        <button
          className="primary"
          onClick={() => {
            setCreating(true);
            setEditing(null);
            setForm({
              code: '', name_uk: '', has_vat: true,
              profit_tax_rate: '', single_tax_rate: '', description: '',
            });
            setError(null);
          }}
        >
          + Додати
        </button>
      </div>

      {error && <div style={{ color: '#b91c1c', padding: '0.5rem' }}>{error}</div>}

      {(creating || editing) && (
        <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
          <h3>{editing ? 'Редагування' : 'Нова схема'}</h3>
          <div className="field">
            <label>Код</label>
            <input
              value={form.code}
              disabled={!!editing}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              data-testid="ts-code"
            />
          </div>
          <div className="field">
            <label>Назва</label>
            <input
              value={form.name_uk}
              onChange={(e) => setForm({ ...form, name_uk: e.target.value })}
            />
          </div>
          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={form.has_vat}
                onChange={(e) => setForm({ ...form, has_vat: e.target.checked })}
              />
              Платник ПДВ
            </label>
          </div>
          <div className="field">
            <label>Ставка податку на прибуток (%)</label>
            <input
              type="number"
              step="0.01"
              value={form.profit_tax_rate}
              onChange={(e) => setForm({ ...form, profit_tax_rate: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Ставка єдиного податку (%)</label>
            <input
              type="number"
              step="0.01"
              value={form.single_tax_rate}
              onChange={(e) => setForm({ ...form, single_tax_rate: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Опис</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div>
            <button onClick={() => { setCreating(false); setEditing(null); }}>Скасувати</button>
            <button
              className="primary"
              onClick={() => editing ? updateMut.mutate() : createMut.mutate()}
              disabled={!form.code || !form.name_uk}
            >
              Зберегти
            </button>
          </div>
        </div>
      )}

      <DataTable<TaxationScheme>
        rows={list.data ?? []}
        columns={cols}
        rowKey={(s) => s.id}
        loading={list.isLoading}
        emptyText="Немає схем оподаткування."
        initialSortKey="code"
      />
    </div>
  );
}

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { detail?: unknown } } };
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return 'Помилка';
}
